/**
 * Transaction State Tracking Service
 * Tracks transaction state to detect and recover from partial saves
 */

import { supabase } from './supabase'

/**
 * Transaction states
 */
export const TRANSACTION_STATES = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
  ROLLED_BACK: 'rolled_back',
  PARTIAL: 'partial', // Some operations succeeded but not all
}

/**
 * Start tracking a transaction
 */
export const startTransactionTracking = async (
  transactionId,
  organizationId,
  userId,
  transactionType,
  operations
) => {
  try {
    const { data, error } = await supabase
      .from('transaction_states')
      .insert({
        id: transactionId,
        organization_id: organizationId,
        user_id: userId,
        transaction_type: transactionType,
        state: TRANSACTION_STATES.PENDING,
        operations: operations.map((op, index) => ({
          index,
          table: op.table,
          action: op.action,
          status: 'pending',
        })),
        started_at: new Date().toISOString(),
      })
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error starting transaction tracking:', error)
    return null
  }
}

/**
 * Update transaction operation status
 */
export const updateTransactionOperation = async (
  transactionId,
  operationIndex,
  status,
  result = null,
  error = null
) => {
  try {
    // Get current transaction state
    const { data: transaction, error: fetchError } = await supabase
      .from('transaction_states')
      .select('*')
      .eq('id', transactionId)
      .single()

    if (fetchError) throw fetchError

    // Update operation status
    const operations = transaction.operations || []
    operations[operationIndex] = {
      ...operations[operationIndex],
      status,
      result,
      error: error?.message || null,
      completed_at: status === 'completed' || status === 'failed' ? new Date().toISOString() : null,
    }

    // Update transaction state
    const allCompleted = operations.every((op) => op.status === 'completed' || op.status === 'failed')
    const anyFailed = operations.some((op) => op.status === 'failed')
    const anyCompleted = operations.some((op) => op.status === 'completed')

    let newState = transaction.state
    if (allCompleted && !anyFailed) {
      newState = TRANSACTION_STATES.COMPLETED
    } else if (allCompleted && anyFailed) {
      newState = TRANSACTION_STATES.PARTIAL
    } else if (anyFailed && anyCompleted) {
      newState = TRANSACTION_STATES.PARTIAL
    } else if (transaction.state === TRANSACTION_STATES.PENDING) {
      newState = TRANSACTION_STATES.IN_PROGRESS
    }

    const { data, error: updateError } = await supabase
      .from('transaction_states')
      .update({
        state: newState,
        operations,
        updated_at: new Date().toISOString(),
      })
      .eq('id', transactionId)
      .select()
      .single()

    if (updateError) throw updateError
    return data
  } catch (error) {
    console.error('Error updating transaction operation:', error)
    return null
  }
}

/**
 * Mark transaction as completed
 */
export const completeTransaction = async (transactionId, results) => {
  try {
    const { data, error } = await supabase
      .from('transaction_states')
      .update({
        state: TRANSACTION_STATES.COMPLETED,
        completed_at: new Date().toISOString(),
        results,
      })
      .eq('id', transactionId)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error completing transaction:', error)
    return null
  }
}

/**
 * Mark transaction as failed
 */
export const failTransaction = async (transactionId, error, rollbackInfo = null) => {
  try {
    const { data, error: updateError } = await supabase
      .from('transaction_states')
      .update({
        state: TRANSACTION_STATES.FAILED,
        failed_at: new Date().toISOString(),
        error_message: error?.message || 'Unknown error',
        rollback_info: rollbackInfo,
      })
      .eq('id', transactionId)
      .select()
      .single()

    if (updateError) throw updateError
    return data
  } catch (error) {
    console.error('Error failing transaction:', error)
    return null
  }
}

/**
 * Mark transaction as rolled back
 */
export const markTransactionRolledBack = async (transactionId, rollbackInfo) => {
  try {
    const { data, error } = await supabase
      .from('transaction_states')
      .update({
        state: TRANSACTION_STATES.ROLLED_BACK,
        rolled_back_at: new Date().toISOString(),
        rollback_info: rollbackInfo,
      })
      .eq('id', transactionId)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error marking transaction as rolled back:', error)
    return null
  }
}

/**
 * Get transaction state
 */
export const getTransactionState = async (transactionId) => {
  try {
    const { data, error } = await supabase
      .from('transaction_states')
      .select('*')
      .eq('id', transactionId)
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error getting transaction state:', error)
    return null
  }
}

/**
 * Detect partial saves
 */
export const detectPartialSaves = async (organizationId, hours = 24) => {
  try {
    const cutoffTime = new Date()
    cutoffTime.setHours(cutoffTime.getHours() - hours)

    const { data, error } = await supabase
      .from('transaction_states')
      .select('*')
      .eq('organization_id', organizationId)
      .in('state', [TRANSACTION_STATES.PARTIAL, TRANSACTION_STATES.IN_PROGRESS, TRANSACTION_STATES.FAILED])
      .gte('started_at', cutoffTime.toISOString())
      .order('started_at', { ascending: false })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error detecting partial saves:', error)
    return []
  }
}

/**
 * Cleanup partial saves
 */
export const cleanupPartialSaves = async (transactionId, rollbackOperations) => {
  try {
    // Get transaction state
    const transaction = await getTransactionState(transactionId)
    if (!transaction) return { success: false, error: 'Transaction not found' }

    // Rollback completed operations
    const completedOperations = (transaction.operations || []).filter(
      (op) => op.status === 'completed'
    )

    for (const op of completedOperations.reverse()) {
      const rollback = rollbackOperations[op.index]
      if (rollback) {
        try {
          switch (rollback.action) {
            case 'delete':
              await supabase.from(rollback.table).delete().match(rollback.filters)
              break
            case 'update':
              await supabase
                .from(rollback.table)
                .update(rollback.data)
                .match(rollback.filters)
              break
            case 'insert':
              await supabase.from(rollback.table).insert(rollback.data)
              break
          }
        } catch (rollbackError) {
          console.error(`Failed to rollback operation ${op.index}:`, rollbackError)
        }
      }
    }

    // Mark transaction as rolled back
    await markTransactionRolledBack(transactionId, {
      cleaned_up_at: new Date().toISOString(),
      operations_rolled_back: completedOperations.length,
    })

    return { success: true, operationsRolledBack: completedOperations.length }
  } catch (error) {
    console.error('Error cleaning up partial saves:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Recover from partial save
 */
export const recoverFromPartialSave = async (transactionId) => {
  try {
    const transaction = await getTransactionState(transactionId)
    if (!transaction) {
      return { success: false, error: 'Transaction not found' }
    }

    if (transaction.state === TRANSACTION_STATES.COMPLETED) {
      return { success: true, message: 'Transaction already completed' }
    }

    if (transaction.state === TRANSACTION_STATES.ROLLED_BACK) {
      return { success: true, message: 'Transaction already rolled back' }
    }

    // Get rollback info
    const rollbackInfo = transaction.rollback_info || {}

    // Attempt cleanup
    const cleanupResult = await cleanupPartialSaves(transactionId, rollbackInfo.operations || [])

    return cleanupResult
  } catch (error) {
    console.error('Error recovering from partial save:', error)
    return { success: false, error: error.message }
  }
}

export default {
  startTransactionTracking,
  updateTransactionOperation,
  completeTransaction,
  failTransaction,
  markTransactionRolledBack,
  getTransactionState,
  detectPartialSaves,
  cleanupPartialSaves,
  recoverFromPartialSave,
  TRANSACTION_STATES,
}

